import { useState } from 'react';
import { Alert, Button, Form, InputGroup, Spinner } from 'react-bootstrap';
import { FaCheck, FaTicketAlt, FaTimes } from 'react-icons/fa';

/**
 * Componente para entrada e validação de cupons de desconto
 */
export const CouponInput = ({ 
  planId, 
  onCouponApplied, 
  onCouponRemoved, 
  disabled = false 
}) => {
  const [code, setCode] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);

  const handleInputChange = (e) => {
    const value = e.target.value.toUpperCase();
    setCode(value);
    // Limpar resultados anteriores ao editar
    if (validationResult) {
      setValidationResult(null);
    }
  };

  const handleApply = async () => {
    if (!code.trim()) {
      setValidationResult({
        success: false,
        message: 'Digite um código de cupom'
      });
      return;
    }

    setIsValidating(true);
    setValidationResult(null);

    try {
      const result = await onCouponApplied(code);
      
      if (result.success) {
        setValidationResult({
          success: true,
          message: 'Cupom aplicado com sucesso!',
          data: result.data
        });
      } else {
        setValidationResult({
          success: false,
          message: result.error || 'Cupom inválido'
        });
      }
    } catch (error) {
      setValidationResult({
        success: false,
        message: 'Erro ao validar cupom'
      });
    } finally {
      setIsValidating(false);
    }
  };

  const handleRemove = () => {
    setCode('');
    setValidationResult(null);
    onCouponRemoved();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !disabled && !isValidating && code.trim()) {
      handleApply();
    }
  };

  const isApplied = validationResult?.success;

  return (
    <div className="mb-3">
      <div className="d-flex align-items-center mb-2">
        <FaTicketAlt className="me-2 text-warning" size={18} />
        <strong>Tem um cupom de desconto?</strong>
      </div>

      <InputGroup>
        <InputGroup.Text>
          <FaTicketAlt className="text-muted" />
        </InputGroup.Text>
        <Form.Control
          type="text"
          placeholder="Digite o código do cupom"
          value={code}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          disabled={disabled || isValidating || isApplied}
          style={{ 
            textTransform: 'uppercase',
            fontWeight: '600',
            letterSpacing: '0.5px'
          }}
        />
        
        {!isApplied ? (
          <Button
            variant="warning"
            onClick={handleApply}
            disabled={disabled || isValidating || !code.trim()}
            style={{ minWidth: '100px' }}
          >
            {isValidating ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-1"
                />
                Validando...
              </>
            ) : (
              'Aplicar'
            )}
          </Button>
        ) : (
          <Button
            variant="outline-danger"
            onClick={handleRemove}
            disabled={disabled}
            style={{ minWidth: '100px' }}
          >
            <FaTimes className="me-1" />
            Remover
          </Button>
        )}
      </InputGroup>

      {/* Mensagens de Validação */}
      {validationResult && (
        <Alert 
          variant={validationResult.success ? 'success' : 'danger'}
          className="mt-2 mb-0 d-flex align-items-center"
          style={{ 
            fontSize: '0.9rem',
            padding: '0.5rem 0.75rem'
          }}
        >
          {validationResult.success ? (
            <FaCheck className="me-2" />
          ) : (
            <FaTimes className="me-2" />
          )}
          <span>{validationResult.message}</span>
        </Alert>
      )}

      {/* Informações do Cupom Aplicado */}
      {isApplied && validationResult.data?.coupon && (
        <div 
          className="mt-2 p-2 bg-light border rounded"
          style={{ fontSize: '0.85rem' }}
        >
          <div className="d-flex justify-content-between">
            <span className="text-muted">Código:</span>
            <strong>{validationResult.data.coupon.code}</strong>
          </div>
          {validationResult.data.coupon.description && (
            <div className="mt-1 text-muted">
              {validationResult.data.coupon.description}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
